import React, { useState, useRef, useEffect } from 'react';
import { useDrag, useDrop } from 'react-dnd';
import {
  PencilIcon,
  TrashIcon,
  EyeIcon,
  EyeSlashIcon,
  ExclamationTriangleIcon,
  Bars3Icon
} from '@heroicons/react/24/outline';
import { FormField } from '../../types/formBuilder';
import { useLanguage } from '../../contexts/LanguageContext';
import ConfirmDialog from '../common/ConfirmDialog';

interface FieldRendererProps {
  field: FormField;
  isSelected: boolean;
  onSelect: () => void;
  onUpdate: (updates: Partial<FormField>) => void;
  onDelete: () => void;
  onMoveField?: (dragIndex: number, hoverIndex: number, targetSectionId: string, sourceSectionId?: string) => void;
  fieldIndex?: number;
  sectionId?: string;
  isPreview?: boolean;
}

const FieldRenderer: React.FC<FieldRendererProps> = ({
  field,
  isSelected,
  onSelect,
  onUpdate,
  onDelete,
  onMoveField,
  fieldIndex = 0,
  sectionId = '',
  isPreview = false
}) => {
  const { t } = useLanguage();
  const [showActions, setShowActions] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const moveTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [fieldElement, setFieldElement] = useState<HTMLDivElement | null>(null);

  // Auto-scroll to field when selected
  useEffect(() => {
    if (isSelected && fieldElement) {
      fieldElement.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
        inline: 'nearest'
      });
    }
  }, [isSelected, fieldElement]);

  // Drag and drop for field reordering
  const [{ isDragging }, dragRef] = useDrag({
    type: 'FORM_FIELD',
    item: { fieldIndex, fieldId: field.id, sectionId },
    collect: (monitor: any) => ({
      isDragging: monitor.isDragging(),
    }),
    canDrag: !isPreview && !!onMoveField,
  });

  const [{ isOverField }, dropRef] = useDrop({
    accept: 'FORM_FIELD',
    hover: (item: { fieldIndex: number, fieldId: string, sectionId: string }, monitor) => {
      if (!onMoveField) return;
      
      const dragIndex = item.fieldIndex;
      const hoverIndex = fieldIndex;
      const dragSectionId = item.sectionId;
      const hoverSectionId = sectionId;

      // Same section reordering
      if (dragSectionId === hoverSectionId) {
        // Don't replace items with themselves
        if (dragIndex === hoverIndex) return;

        // Clear existing timeout
        if (moveTimeoutRef.current) {
          clearTimeout(moveTimeoutRef.current);
        }

        // Throttle the move operation
        moveTimeoutRef.current = setTimeout(() => {
          onMoveField(dragIndex, hoverIndex, sectionId);
          item.fieldIndex = hoverIndex;
        }, 50);
      }
      // Cross-section move - handled by drop event
    },
    drop: (item: { fieldIndex: number, fieldId: string, sectionId: string }, monitor) => {
      if (!onMoveField) return;
      
      const dragSectionId = item.sectionId;
      const dropSectionId = sectionId;
      
      // Cross-section move
      if (dragSectionId !== dropSectionId) {
        // Move field to different section at this position
        onMoveField(item.fieldIndex, fieldIndex, dropSectionId, dragSectionId);
        return { moved: true };
      }
    },
    collect: (monitor: any) => ({
      isOverField: monitor.isOver(),
    }),
  });

  // Combine refs
  const combinedRef = (node: HTMLDivElement | null) => {
    dragRef(node);
    dropRef(node);
  };

  const getFieldWidth = () => {
    switch (field.width) {
      case 'half': return 'md:col-span-1';
      case 'third': return 'md:col-span-1 lg:col-span-1';
      case 'quarter': return 'md:col-span-1 lg:col-span-1 xl:col-span-1';
      default: return 'md:col-span-2';
    }
  };

  const renderFieldInput = () => {
    const baseClasses = `
      w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md
      bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100
      placeholder-gray-500 dark:placeholder-gray-400
      focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500
      ${isPreview ? '' : 'pointer-events-none'}
    `;

    switch (field.type) {
      case 'text':
      case 'email':
      case 'phone':
        return (
          <input
            type={field.type === 'email' ? 'email' : field.type === 'phone' ? 'tel' : 'text'}
            placeholder={field.placeholder || field.label}
            className={baseClasses}
            disabled={!isPreview}
          />
        );

      case 'number':
      case 'currency':
        return (
          <input
            type="number"
            placeholder={field.placeholder || field.label}
            min={field.config?.min}
            max={field.config?.max}
            step={field.config?.step}
            className={baseClasses}
            disabled={!isPreview}
          />
        );

      case 'date':
        return (
          <input
            type="date"
            className={baseClasses}
            disabled={!isPreview}
          />
        );

      case 'time':
        return (
          <input
            type="time"
            className={baseClasses}
            disabled={!isPreview}
          />
        );

      case 'datetime':
        return (
          <input
            type="datetime-local"
            className={baseClasses}
            disabled={!isPreview}
          />
        );

      case 'textarea':
        return (
          <textarea
            placeholder={field.placeholder || field.label}
            rows={3}
            maxLength={field.config?.maxLength}
            className={baseClasses}
            disabled={!isPreview}
          />
        );

      case 'select':
        return (
          <select className={baseClasses} disabled={!isPreview}>
            <option value="">{field.placeholder || `Select ${field.label}`}</option>
            {field.options?.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        );

      case 'multiselect':
        return (
          <div className="space-y-2">
            {field.options?.map(option => (
              <label key={option.value} className="flex items-center">
                <input
                  type="checkbox"
                  value={option.value}
                  className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                  disabled={!isPreview}
                />
                <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                  {option.label}
                </span>
              </label>
            ))}
          </div>
        );

      case 'radio':
        return (
          <div className="space-y-2">
            {field.options?.map(option => (
              <label key={option.value} className="flex items-center">
                <input
                  type="radio"
                  name={field.name}
                  value={option.value}
                  className="border-gray-300 text-primary-600 focus:ring-primary-500"
                  disabled={!isPreview}
                />
                <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                  {option.label}
                </span>
              </label>
            ))}
          </div>
        );

      case 'checkbox':
        return (
          <div className="space-y-2">
            {field.options?.map(option => (
              <label key={option.value} className="flex items-center">
                <input
                  type="checkbox"
                  value={option.value}
                  className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                  disabled={!isPreview}
                />
                <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                  {option.label}
                </span>
              </label>
            ))}
          </div>
        );

      case 'switch':
        return (
          <label className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              className="sr-only peer"
              disabled={!isPreview}
            />
            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 dark:peer-focus:ring-primary-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-primary-600"></div>
          </label>
        );

      case 'file':
      case 'image':
        return (
          <div className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-4 text-center">
            <div className="text-sm text-gray-500 dark:text-gray-400">
              {field.type === 'image' ? 'Click to upload image' : 'Click to upload file'}
            </div>
          </div>
        );

      case 'color':
        return (
          <input
            type="color"
            className="h-10 w-20 border border-gray-300 dark:border-gray-600 rounded cursor-pointer"
            disabled={!isPreview}
          />
        );

      case 'range':
        return (
          <div className="space-y-2">
            <input
              type="range"
              min={field.config?.min || 0}
              max={field.config?.max || 100}
              className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer dark:bg-gray-700"
              disabled={!isPreview}
            />
            <div className="flex justify-between text-xs text-gray-500">
              <span>{field.config?.min || 0}</span>
              <span>{field.config?.max || 100}</span>
            </div>
          </div>
        );

      case 'rating':
        return (
          <div className="flex space-x-1">
            {Array.from({ length: field.config?.max || 5 }).map((_, i) => (
              <button
                key={i}
                type="button"
                className="text-gray-300 hover:text-yellow-400 text-xl"
                disabled={!isPreview}
              >
                ★
              </button>
            ))}
          </div>
        );

      case 'signature':
        return (
          <div className="border border-gray-300 dark:border-gray-600 rounded-lg h-32 bg-gray-50 dark:bg-gray-800 flex items-center justify-center">
            <span className="text-gray-500 dark:text-gray-400 text-sm">Signature pad</span>
          </div>
        );

      case 'customer_lookup':
      case 'product_lookup':
      case 'user_lookup':
      case 'organization_lookup':
        return (
          <div className="relative">
            <input
              type="text"
              placeholder={`Search ${field.type.replace('_lookup', '')}...`}
              className={baseClasses}
              disabled={!isPreview}
            />
            <div className="absolute right-2 top-2 text-gray-400">
              🔍
            </div>
          </div>
        );

      default:
        return (
          <div className="p-4 bg-gray-100 dark:bg-gray-800 rounded border-2 border-dashed border-gray-300 dark:border-gray-600">
            <div className="flex items-center justify-center text-gray-500 dark:text-gray-400">
              <ExclamationTriangleIcon className="h-5 w-5 mr-2" />
              <span className="text-sm">Unsupported field type: {field.type}</span>
            </div>
          </div>
        );
    }
  };

  return (
    <div
      ref={(node) => {
        setFieldElement(node);
        combinedRef(node);
      }}
      className={`
        relative group ${getFieldWidth()} transition-all duration-200
        ${isSelected ? 'ring-2 ring-primary-500 ring-opacity-75 bg-primary-50 dark:bg-primary-900/20' : ''}
        ${!isPreview ? 'cursor-pointer' : ''}
        ${isDragging ? 'opacity-50 scale-95' : ''}
        ${isOverField ? 'transform translate-y-1 ring-2 ring-blue-300 ring-opacity-50' : ''}
        ${isDragging ? 'cursor-grabbing' : !isPreview && onMoveField ? 'cursor-grab' : ''}
      `}
      onClick={!isPreview ? onSelect : undefined}
      onMouseEnter={() => setShowActions(true)}
      onMouseLeave={() => setShowActions(false)}
    >
      {/* Field Actions */}
      {!isPreview && showActions && (
        <div className="absolute top-0 right-0 z-10 flex space-x-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-bl-md shadow-sm">
          {/* Drag Handle */}
          {onMoveField && (
            <div 
              className="flex items-center justify-center p-1 cursor-grab hover:bg-primary-50 dark:hover:bg-primary-900/20 transition-colors"
              title={t('formBuilder.dragToReorder') || 'Drag to reorder'}
              onMouseDown={(e) => e.stopPropagation()}
            >
              <Bars3Icon className="h-4 w-4 text-gray-400 hover:text-primary-600" />
            </div>
          )}
          
          <button
            onClick={(e) => {
              e.stopPropagation();
              onSelect();
            }}
            className="p-1 text-gray-400 hover:text-primary-600"
            title="Edit field"
          >
            <PencilIcon className="h-4 w-4" />
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              setShowDeleteConfirm(true);
            }}
            className="p-1 text-gray-400 hover:text-red-600"
            title="Delete field"
          >
            <TrashIcon className="h-4 w-4" />
          </button>
        </div>
      )}

      {/* Field Content */}
      <div className="space-y-2">
        {/* Label */}
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
          {field.label}
          {field.required && <span className="text-red-500 ml-1">*</span>}
        </label>

        {/* Description */}
        {field.description && (
          <p className="text-xs text-gray-500 dark:text-gray-400">
            {field.description}
          </p>
        )}

        {/* Input */}
        {renderFieldInput()}

        {/* Validation Messages Preview */}
        {!isPreview && field.validations && field.validations.length > 0 && (
          <div className="text-xs text-gray-500 dark:text-gray-400">
            Validations: {field.validations.map(v => v.rule).join(', ')}
          </div>
        )}
      </div>

      {/* Selection Indicator */}
      {!isPreview && isSelected && (
        <div className="absolute inset-0 border-2 border-primary-500 rounded-md pointer-events-none" />
      )}

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={onDelete}
        title={t('formBuilder.confirmDelete.title') || 'Delete Field'}
        message={t('formBuilder.confirmDelete.message') || 'Are you sure you want to delete this field? This action cannot be undone.'}
        itemName={field.label || field.name}
        confirmText={t('formBuilder.confirmDelete.confirm') || 'Delete Field'}
        cancelText={t('common.buttons.cancel') || 'Cancel'}
        type="danger"
      />
    </div>
  );
};

export default FieldRenderer;
